import {StemDate, type DateInput} from "../time/Date";
import {isFunction, isString} from "../base/Utils";
import {type StoreObject} from "./Store";
import {type LegacyPropertyDescriptor} from "../decorators/Utils";
import {GlobalState, type StoreId} from "./State";

export interface FieldOptions {
    rawField?: string | symbol | ((key: string, descriptor?: FieldDescriptor) => string | symbol);
    cacheField?: boolean | symbol;
    loader?: FieldLoader;
    isReadOnly?: boolean;
    // Whether the store this points into is guaranteed to have loaded. Defaults to whether the owning store
    // declares it a dependency, which is the only thing that makes the guarantee true
    guaranteed?: boolean;
}

export interface StoreObjectWithFields extends StoreObject {
    fieldDescriptors?: FieldDescriptor[];
}

// What a spec's loader is: the raw value off the wire, and the object it is being read on
export type FieldLoader = (value: any, obj: StoreObjectWithFields) => unknown;

// The hook a spec may carry to load its own values: BaseEnum declares one, and Date is given one below
export interface FieldLoaderSource {
    makeFieldLoader?: (descriptor: FieldDescriptor) => FieldLoader;
}

// What @field takes: the name of a store, or a class - a primitive wrapper included. The `| any` this used
// to end in collapsed the union, so none of the members said anything
export type FieldType = string | (abstract new (...args: any[]) => unknown);

// The value a spec loads into: @field(Date) reads back a StemDate, @field(User) a User.
// ts-plugin/ declares un-annotated @field members with this, which is what makes the decorator imply the type.
// A field written `foo?` arrives here as `Spec | null`, since a loader hands back what it was given when
// there's nothing to load (StemDate.optionally, Store.get).
// A primitive wrapper has to be answered before the generic constructor branch, which would otherwise infer the
// wrapper object (String, not string) - what the field actually holds is the primitive.
export type FieldValue<Spec> =
    Spec extends null ? null :
    Spec extends DateConstructor ? StemDate :
    Spec extends StringConstructor ? string :
    Spec extends NumberConstructor ? number :
    Spec extends BooleanConstructor ? boolean :
    Spec extends ArrayConstructor ? any[] :
    Spec extends abstract new (...args: any[]) => infer Instance ? Instance :
    Spec extends string ? StoreObject :
    never;

type StoreConstructor = abstract new (...args: any[]) => StoreObject;

// A foreign key keeps its raw value on the instance as `<key>Id` (see makeDescriptor); every other
// spec reads the raw value out of a symbol, so there's nothing extra to declare for it.
type FieldRawIdKey<Key extends string, Spec> = Spec extends StoreConstructor | string ? `${Key}Id` : never;

// The id the store being pointed at declares, so a foreign key is no wider than the object it names: a
// store that says `declare id: number` gives every key into it a number. A name goes through the registry,
// and anything neither - "self" among them - keeps the open StoreId
type FieldRawIdValue<Spec> =
    NonNullable<Spec> extends abstract new (...args: any[]) => {id: infer Id} ? Id :
    NonNullable<Spec> extends keyof StemStoreRegistry ? StemStoreRegistry[NonNullable<Spec>]["id"] :
    StoreId;

export type FieldRawIds<Specs extends Record<string, unknown>, Omitted extends string = never> = Omit<{
    [Key in keyof Specs as FieldRawIdKey<Key & string, Specs[Key]>]:
        null extends Specs[Key] ? FieldRawIdValue<Specs[Key]> | null : FieldRawIdValue<Specs[Key]>;
}, Omitted>;

// Checks a hand-written annotation against what the spec loads, for the fields the plugin leaves alone.
// Optional since plenty of fields are declared `foo?: Bar` or `foo?: Bar | null`.
export type FieldDecorator<Spec> = <Key extends string>(targetProto: {[K in Key]?: FieldValue<Spec> | null}, key: Key) => void;

export class FieldDescriptor {
    type: FieldType & FieldLoaderSource;
    targetProto?: StoreObjectWithFields;
    key?: string;
    rawDescriptor?: LegacyPropertyDescriptor;
    rawField?: string | symbol | ((key: string, descriptor?: FieldDescriptor) => string | symbol);
    cacheField?: false | symbol;
    loader?: FieldLoader;
    isReadOnly?: boolean;
    guaranteed?: boolean;
    // Object.assign copies the options over, and a descriptor is read by name at runtime
    [key: string]: any;

    constructor(type: FieldType, options: FieldOptions = {}) {
        this.type = type;
        Object.assign(this, options);
    }

    setTarget(targetProto: StoreObjectWithFields, key: string, rawDescriptor?: LegacyPropertyDescriptor): void {
        if (!targetProto.fieldDescriptors) {
            targetProto.fieldDescriptors = [];
        }
        targetProto.fieldDescriptors.push(this);

        this.targetProto = targetProto;
        this.key = key;
        this.rawDescriptor = rawDescriptor;
    }

    // TODO Use this to support lazy initialization
    getDefaultValue(obj: object): unknown {
        const {initializer} = this.rawDescriptor;
        return initializer?.call(obj);
    }

    makeDescriptor(): PropertyDescriptor {
        if (isString(this.type)) {
            // We're a Foreign key
            this.rawField = this.rawField || ((key: string) => key + "Id"); // By default we'll add a suffix
            this.cacheField = false;

            const owner = this.targetProto?.constructor as typeof StoreObject | undefined;
            // "self" points back into the store the field is declared on, which is the one name a spec
            // cannot write as a value - the class binding is not initialized yet when the decorator runs
            const isSelf = this.type === "self";
            const storeName = isSelf ? owner?.objectType : this.type as string;

            // A key into a store the owner declares a dependency on is guaranteed to resolve, since the
            // state loader imports dependencies first. `guaranteed` overrides it in either direction.
            // Never for "self": one store's objects are created in payload order, so a child can precede
            // the parent it points at
            const guaranteed = this.guaranteed ?? (!isSelf && owner?.dependencies?.some(
                dependency => dependency.toLowerCase() === storeName?.toLowerCase()));

            this.loader = (value: any, obj: StoreObjectWithFields) => {
                // TODO Instead of calling GlobalState, the object should ALWAYS implement .getState()
                const store = obj.getStore ? obj.getStore(storeName) : GlobalState.getStore(storeName);
                return guaranteed ? store?.getRequired(value) : store?.get(value);
            }
        }

        // First let the type modify this, in case it needs to overwrite some behavior
        if (!this.loader && this.type.makeFieldLoader) {
            this.loader = this.type.makeFieldLoader(this);
            // We actually prefer by default to have a cache to that object[field] === object[field] (which would have been invalidated by another load)
            this.cacheField = this.cacheField ?? Symbol("cached-" + this.key);
        }

        // Apply default logic in case it was not explicitly given
        if (isFunction(this.rawField)) {
            this.rawField = this.rawField(this.key!, this); // key is always set by setTarget before makeDescriptor runs
        }

        if (!this.rawField) {
            this.rawField = Symbol("_" + this.key);
        } else {
            this.isReadOnly = true;
        }

        // Extracting for speed in the functions bellow
        const {rawField, cacheField, loader, isReadOnly, key} = this;

        return {
            get(this: StoreObjectWithFields & Record<string | symbol, any>): unknown {
                if (cacheField && this[cacheField]) {
                    return this[cacheField];
                }

                const value = this[rawField!];

                if (value == null || !loader) {
                    return value;
                }

                const result = loader(value, this);
                if (cacheField) {
                    this[cacheField] = result
                }
                return result;
            },
            set(this: StoreObjectWithFields & Record<string | symbol, any>, value: unknown): void {
                if (isReadOnly) {
                    throw `Not allowed to change field ${key}`;
                }
                // TODO type validation
                // if (!(value instanceof type)) {
                //     console.warn("Invalid type detected", key, value);
                // }
                this[rawField] = value;
                if (cacheField) {
                    delete this[cacheField];
                }
            }
        }
    }
}


// TODO Implement a way to say @field(Array, "StoreObject") for instance
// `const Spec` keeps @field("Currency") at the literal type, so the spec survives into FieldValue
export function field<const Spec extends FieldType>(type: Spec, options: FieldOptions = {}): FieldDecorator<Spec> {
    // The actual descriptor - supports both legacy JS decorators and can be gradually migrated to TS
    // Left open: the declared type is FieldDecorator<Spec>, whose mapped targetProto no implementation matches
    return (targetProto: any, name: string, rawDescriptor?: LegacyPropertyDescriptor): PropertyDescriptor => {
        const fieldDescriptor = new FieldDescriptor(type, options);
        fieldDescriptor.setTarget(targetProto, name, rawDescriptor);
        return fieldDescriptor.makeDescriptor();
    };
}

// Date is one of the specs @field takes, so it carries the same loader hook the others declare
declare global {
    interface DateConstructor extends FieldLoaderSource {}
}

// Default handling of objects
Date.makeFieldLoader = (): FieldLoader => {
    return (value: DateInput) => StemDate.optionally(value);
}
